import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { phoneStyles } from '../../styles/phone-styles.js';
import { twilioService } from '../../services/twilio-service.js';
import { phoneService } from '../../services/phone-service.js';
import { AbortableLoader } from '../../services/abortable-loader.js';
import { confirmService } from '../../services/confirm-service.js';
import './phone-number-modal.js';
import './phone-test-modal.js';
import './phone-number-table.js';
import '../voice-modal.js';

export class PhonePage extends LitElement {
  static properties = {
    selectedNumber: { type: Object },
    numbers: { type: Array },
    _loading: { type: Boolean, state: true },
    _searchQuery: { type: String, state: true },
    _sortColumn: { type: String, state: true },
    _sortDirection: { type: String, state: true },
    _currentPage: { type: Number, state: true },
    _pageSize: { type: Number, state: true },
    _totalItems: { type: Number, state: true },
    _totalPages: { type: Number, state: true },
    _showModal: { type: Boolean, state: true },
    _modalMode: { type: String, state: true },
    _editingNumber: { type: Object, state: true },
    _saving: { type: Boolean, state: true },
    _toastVisible: { type: Boolean, state: true },
    _toastMessage: { type: String, state: true },
    _toastType: { type: String, state: true },
    _modalStep: { type: String, state: true },
    _twilioNumbers: { type: Array, state: true },
    _twilioLoading: { type: Boolean, state: true },
    _selectedTwilioNumber: { type: Object, state: true },
    _testCallNumber: { type: Object, state: true },
    _agents: { type: Array, state: true }
  };

  static styles = [designSystem, componentStyles, phoneStyles, css`:host { display: block; }`];

  constructor() {
    super();
    this.selectedNumber = null;
    this.numbers = [];
    this._loading = true;
    this._searchQuery = '';
    this._sortColumn = '';
    this._sortDirection = 'asc';
    this._currentPage = 1;
    this._pageSize = 20;
    this._totalItems = 0;
    this._totalPages = 1;
    this._showModal = false;
    this._modalMode = 'add';
    this._editingNumber = {};
    this._saving = false;
    this._toastVisible = false;
    this._toastMessage = '';
    this._toastType = 'success';
    this._webhookConfigLoading = false;
    this._agents = [];
    this._loader = new AbortableLoader();
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadNumbers();
    this._loadAgents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._loader.abort();
  }

  /* ─── Data Loading (delegates to phoneService) ─── */

  async _loadNumbers() {
    this._loading = true;

    const result = await this._loader.run(signal =>
      phoneService.loadNumbers({
        page: this._currentPage,
        pageSize: this._pageSize,
        search: this._searchQuery,
        sortColumn: this._sortColumn,
        sortDirection: this._sortDirection
      }, { signal })
    );

    if (result.aborted) return;

    if (result.error) {
      if (result.error.message !== 'Session expired') {
        console.error('[PhoneNumbers] Load error:', result.error);
        this._showToast('Network error loading numbers', 'error');
      }
    } else if (result.data.success) {
      this.numbers = result.data.numbers.map(n => phoneService.formatForUI(n));
      if (result.data.pagination) {
        this._currentPage = result.data.pagination.page;
        this._totalItems = result.data.pagination.total;
        this._totalPages = result.data.pagination.pages;
      }
    } else {
      this._showToast(result.data.error || 'Failed to load numbers', 'error');
    }

    this._loading = false;
  }

  /* ─── Table Event Handlers (from phone-number-table sub-component) ─── */

  _handleSearch(query) {
    this._searchQuery = query;
    this._currentPage = 1;
    this._loadNumbers();
  }

  _handleSort(column) {
    if (this._sortColumn === column) {
      this._sortDirection = this._sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this._sortColumn = column;
      this._sortDirection = 'asc';
    }
    this._currentPage = 1;
    this._loadNumbers();
  }

  _handlePageChange(page) {
    this._currentPage = page;
    this._loadNumbers();
  }

  _toggleExpand(number) {
    this.selectedNumber = this.selectedNumber?.id === number.id ? null : number;
  }

  /* ─── Add / Edit Modal ─── */

  _openAddModal() {
    this._modalMode = 'add';
    this._modalStep = 'twilio-list';
    this._twilioNumbers = [];
    this._twilioLoading = false;
    this._selectedTwilioNumber = null;
    this._editingNumber = {};
    this._showModal = true;
    this._fetchTwilioNumbers();
  }

  _openEditModal(num) {
    this._modalMode = 'edit';
    this._modalStep = 'config-form';
    this._selectedTwilioNumber = null;
    this._editingNumber = {
      id: num.id,
      twilio_number: num.raw,
      label: num.label,
      number_type: num.type,
      country_code: num.country,
      ujid: num.ujid || '',
      park_name: num.parkName,
      agent_name: num.agent.name === 'Unassigned' ? '' : num.agent.name,
      status: num.status,
      monthly_cost: num.monthlyCost || '',
      caller_id_name: num.callerIdName,
      caller_id_enabled: num.callerIdEnabled,
      timezone: num.timezone,
      routing_rules: num.routingRules,
      notes: num.notes
    };
    this._showModal = true;
  }

  _closeModal() {
    this._showModal = false;
    this._editingNumber = {};
    this._modalStep = 'twilio-list';
    this._twilioNumbers = [];
    this._twilioLoading = false;
    this._selectedTwilioNumber = null;
  }

  _updateField(field, value) {
    this._editingNumber = { ...this._editingNumber, [field]: value };
  }

  async _saveModal() {
    if (this._saving) return;

    const data = this._editingNumber;
    if (!data.twilio_number) {
      this._showToast('Phone number is required', 'error');
      return;
    }

    // Clean up - convert empty strings to proper types
    const payload = { ...data };
    if (payload.ujid === '') payload.ujid = 0;
    else payload.ujid = parseInt(payload.ujid, 10) || 0;
    if (payload.monthly_cost === '') payload.monthly_cost = 0;
    else payload.monthly_cost = parseFloat(payload.monthly_cost) || 0;

    this._saving = true;
    try {
      const result = this._modalMode === 'add'
        ? await phoneService.createNumber(payload)
        : await phoneService.updateNumber(payload);

      if (result.success) {
        this._showToast(this._modalMode === 'add' ? 'Phone number added' : 'Phone number updated');
        const savedId = result.id || payload.id;
        this._closeModal();
        await this._loadNumbers();
        // Auto-configure webhook for this number
        if (savedId) {
          twilioService.configureWebhooks([savedId]).catch(() => {});
        }
      } else {
        this._showToast(result.error || 'Save failed', 'error');
      }
    } catch (err) {
      this._showToast('Network error saving', 'error');
    } finally {
      this._saving = false;
    }
  }

  /* ─── Delete ─── */

  async _confirmDeleteNumber(num) {
    const confirmed = await confirmService.show({
      title: 'Delete Phone Number',
      description: `Are you sure you want to delete ${num.phone}${num.label ? ` (${num.label})` : ''}? This action cannot be undone.`,
    });
    if (!confirmed) return;

    try {
      const result = await phoneService.deleteNumber(num.id);
      if (result.success) {
        this._showToast('Phone number deleted');
        if (this.selectedNumber?.id === num.id) this.selectedNumber = null;
        await this._loadNumbers();
      } else {
        this._showToast(result.error || 'Delete failed', 'error');
      }
    } catch (err) {
      this._showToast('Network error deleting', 'error');
    }
  }

  /* ─── Test Call ─── */

  _openTestCallDialog(num) {
    this._testCallNumber = num;
  }

  _closeTestCallDialog() {
    this._testCallNumber = null;
  }

  /* ─── Notes Auto-save ─── */

  async _saveNotes(numberId, notes) {
    const num = this.numbers.find(n => n.id === numberId);
    if (!num || notes === num.notes) return;

    try {
      const result = await phoneService.saveNotes(numberId, notes);
      if (result.success) {
        this.numbers = this.numbers.map(n => n.id === numberId ? { ...n, notes } : n);
        if (this.selectedNumber?.id === numberId) {
          this.selectedNumber = { ...this.selectedNumber, notes };
        }
      }
    } catch (err) {
      console.error('[PhoneNumbers] Notes save error:', err);
    }
  }

  /* ─── Toast ─── */

  _showToast(message, type = 'success') {
    this._toastMessage = message;
    this._toastType = type;
    this._toastVisible = true;
    setTimeout(() => { this._toastVisible = false; }, 4000);
  }

  /* ─── Webhook & Agent ─── */

  async _loadAgents() {
    try {
      this._agents = await phoneService.loadAgents();
    } catch (e) {
      console.error('[PhoneNumbers] Failed to load agents:', e);
    }
  }

  async _assignAgent(numberId, agentId) {
    try {
      await phoneService.assignAgent(numberId, agentId);
      this._showToast('Agent assigned');
      this._loadNumbers();
      // Auto-configure webhook to sync routing
      twilioService.configureWebhooks([numberId]).catch(() => {});
    } catch (e) {
      if (e.message !== 'Session expired') {
        this._showToast('Failed to assign agent', 'error');
      }
    }
  }

  /* ─── Render ─── */

  render() {
    return html`
      <phone-number-table
        .numbers=${this.numbers}
        .agents=${this._agents}
        .loading=${this._loading}
        .searchQuery=${this._searchQuery}
        .sortColumn=${this._sortColumn}
        .sortDirection=${this._sortDirection}
        .currentPage=${this._currentPage}
        .pageSize=${this._pageSize}
        .totalItems=${this._totalItems}
        .totalPages=${this._totalPages}
        .selectedNumber=${this.selectedNumber}
        @add-number=${this._openAddModal}
        @search=${(e) => this._handleSearch(e.detail.query)}
        @sort=${(e) => this._handleSort(e.detail.column)}
        @page-change=${(e) => this._handlePageChange(e.detail.page)}
        @toggle-expand=${(e) => this._toggleExpand(e.detail.number)}
        @edit-number=${(e) => this._openEditModal(e.detail.number)}
        @delete-number=${(e) => this._confirmDeleteNumber(e.detail.number)}
        @test-call=${(e) => this._openTestCallDialog(e.detail.number)}
        @assign-agent=${(e) => this._assignAgent(e.detail.numberId, e.detail.agentId)}
        @save-notes=${(e) => this._saveNotes(e.detail.numberId, e.detail.notes)}
      ></phone-number-table>

      <!-- Add/Edit Modal -->
      ${this._renderModal()}

      <!-- Test Call Dialog -->
      ${this._testCallNumber ? html`
        <phone-test-modal
          .number=${this._testCallNumber}
          @close=${this._closeTestCallDialog}
        ></phone-test-modal>
      ` : ''}

      <!-- Toast -->
      ${this._toastVisible ? html`
        <div class="toast ${this._toastType}">
          <i class="fa-sharp fa-light ${this._toastType === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'}"></i>
          ${this._toastMessage}
        </div>
      ` : ''}
    `;
  }

  /* ─── Twilio Import Methods ─── */

  async _fetchTwilioNumbers() {
    this._twilioLoading = true;
    this._twilioNumbers = [];
    try {
      const data = await twilioService.listTwilioNumbers();
      if (data.success) {
        this._twilioNumbers = data.numbers || [];
      } else {
        this._showToast(data.error || 'Failed to fetch Twilio numbers', 'error');
      }
    } catch (err) {
      if (err.message !== 'Session expired') {
        this._showToast('Failed to connect to Twilio', 'error');
      }
    } finally {
      this._twilioLoading = false;
    }
  }

  _selectTwilioNumber(num) {
    if (num.already_imported) return;
    this._selectedTwilioNumber = num;
  }

  _confirmTwilioSelection() {
    const num = this._selectedTwilioNumber;
    if (!num) return;

    // Auto-detect number type from phone number
    const phone = num.phone_number || '';
    let numberType = 'Local';
    if (phone.startsWith('+1800') || phone.startsWith('+1888') || phone.startsWith('+1877') ||
        phone.startsWith('+1866') || phone.startsWith('+1855') || phone.startsWith('+1844') ||
        phone.startsWith('+1833') || phone.startsWith('+1822')) {
      numberType = 'Toll-Free';
    }

    // Auto-detect country from prefix
    let country = 'US';
    if (phone.startsWith('+44')) country = 'GB';
    else if (phone.startsWith('+52')) country = 'MX';
    else if (phone.startsWith('+1') && phone.length === 12) {
      // Could be US or CA — default US
      const areaCode = phone.substring(2, 5);
      const canadianAreaCodes = ['204','226','236','249','250','289','306','343','365','387','403','416','418','431','437','438','450','506','514','519','548','579','581','587','604','613','639','647','672','705','709','742','778','780','782','807','819','825','867','873','902','905'];
      if (canadianAreaCodes.includes(areaCode)) country = 'CA';
    }

    this._editingNumber = {
      twilio_number: phone,
      twilio_sid: num.sid || '',
      label: num.friendly_name || '',
      number_type: numberType,
      country_code: country,
      ujid: '',
      park_name: '',
      agent_name: '',
      agent_id: null,
      status: 'active',
      monthly_cost: num.monthly_cost || '',
      caller_id_name: '',
      caller_id_enabled: false,
      timezone: 'America/New_York',
      routing_rules: '',
      notes: ''
    };
    this._modalStep = 'config-form';
  }

  _goBack() {
    if (this._modalStep === 'config-form' && this._modalMode === 'add') {
      this._modalStep = 'twilio-list';
    }
  }

  /* ─── Modal Render ─── */

  _renderModal() {
    const step = this._modalStep || 'twilio-list';
    const isEdit = this._modalMode === 'edit';

    // Dynamic title per step
    let title = 'Add Phone Number';
    if (isEdit) title = 'Edit Phone Number';
    else if (step === 'twilio-list') title = 'Import from Twilio';
    else if (step === 'config-form') title = 'Configure Number';

    return html`
      <voice-modal ?open=${this._showModal} title="${title}" width="580px"
        @modal-close=${this._closeModal}>
        <div style="padding:var(--space-6, 24px);">
          ${step === 'twilio-list' ? this._renderTwilioListBody() : ''}
          ${step === 'config-form' ? this._renderConfigFormBody() : ''}
        </div>

        <div slot="footer">
          ${this._renderModalFooter(step, isEdit)}
        </div>
      </voice-modal>
    `;
  }

  _renderTwilioListBody() {
    if (this._twilioLoading) {
      return html`
        <div class="twilio-loading">
          <i class="fa-sharp fa-solid fa-spinner fa-spin"></i>
          <span>Loading numbers from Twilio...</span>
        </div>
      `;
    }

    if (!this._twilioNumbers || this._twilioNumbers.length === 0) {
      return html`
        <div class="twilio-empty">
          <i class="fa-sharp fa-light fa-phone-slash"></i>
          <span>No phone numbers found in your Twilio account</span>
        </div>
      `;
    }

    return html`
      <div class="twilio-list">
        ${this._twilioNumbers.map(num => {
          const isSelected = this._selectedTwilioNumber?.phone_number === num.phone_number;
          const isImported = num.already_imported;
          return html`
            <div class="twilio-list-item ${isSelected ? 'selected' : ''} ${isImported ? 'disabled' : ''}"
              @click=${() => this._selectTwilioNumber(num)}>
              <div class="twilio-list-radio"></div>
              <div class="twilio-list-info">
                <div class="twilio-list-phone">${num.phone_number}</div>
                <div class="twilio-list-name">${num.friendly_name || 'No label'}</div>
              </div>
              <div class="twilio-list-caps">
                ${num.capabilities?.voice ? html`<span class="twilio-cap-badge voice">Voice</span>` : ''}
                ${num.capabilities?.sms ? html`<span class="twilio-cap-badge sms">SMS</span>` : ''}
                ${num.capabilities?.mms ? html`<span class="twilio-cap-badge mms">MMS</span>` : ''}
                ${isImported ? html`<span class="twilio-cap-badge imported">Imported</span>` : ''}
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }

  _renderConfigFormBody() {
    return html`
      <phone-number-modal
        .data=${this._editingNumber}
        .agents=${this._agents || []}
        .mode=${this._modalMode}
        .isFromTwilio=${!!this._selectedTwilioNumber}
        .saving=${this._saving}
        @field-change=${(e) => this._updateField(e.detail.field, e.detail.value)}
      ></phone-number-modal>
    `;
  }

  _renderModalFooter(step, isEdit) {
    if (step === 'twilio-list') {
      return html`
        <button class="btn btn-secondary" @click=${this._closeModal}>Cancel</button>
        <button class="btn btn-primary" @click=${this._confirmTwilioSelection}
          ?disabled=${!this._selectedTwilioNumber}>
          Next
        </button>
      `;
    }

    // config-form
    return html`
      ${!isEdit ? html`
        <button class="btn btn-ghost" style="margin-right:auto;" @click=${this._goBack}>
          <i class="fa-sharp fa-light fa-arrow-left"></i> Back
        </button>
      ` : ''}
      <button class="btn btn-secondary" @click=${this._closeModal}>Cancel</button>
      <button class="btn btn-primary" @click=${this._saveModal} ?disabled=${this._saving}>
        ${this._saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Save Number')}
      </button>
    `;
  }
}

customElements.define('phone-page', PhonePage);

